import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ReferenceLine,
} from "recharts";
import { color } from "../../utils/theme";
import { Box, Center, HStack, VStack, Text, Divider } from "@chakra-ui/react";

export interface RunChartDataType {
  time: string;
  performance: number;
  Median: number;
}

export interface RunChartProps {
  data: RunChartDataType[];
  project?: string;
  measurement?: string;
  isOnDashboard?: boolean;
  upperLimit?: number;
  lowerLimit?: number;
  chartInterpretation?: string;
  chartInterpretationDescription?: string[];
  showInterpretation?: boolean;
}

const RunChart = (prop: RunChartProps) => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth * (prop.isOnDashboard ? 0.38 : 0.7),
    height: window.innerHeight * (prop.isOnDashboard ? 0.45 : 0.7),
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Center>
      <VStack gap={10}>
        {prop.project && prop.measurement && (
          <h2
            style={{
              textAlign: "center",
              marginBottom: "20px",
              color: color.text,
              fontWeight: "bold",
            }}
          >
            {prop.project.toUpperCase()}/{prop.measurement.toUpperCase()} Run
            Chart
          </h2>
        )}
        <Divider />
        <HStack w={"80%"}>
          <LineChart
            width={dimensions.width}
            height={dimensions.height}
            data={prop.data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 10, // Increased bottom margin
            }}
          >
            <CartesianGrid strokeDasharray="5 5" />
            <XAxis dataKey="time">
              <Label
                value="Time"
                offset={-20}
                position="insideBottomLeft"
                color={color.primary}
                fontWeight={"bold"}
              />
            </XAxis>
            <YAxis
              domain={[
                (dataMin: number) => {
                  const limit =
                    typeof prop.lowerLimit !== "undefined" &&
                    prop.lowerLimit < dataMin
                      ? prop.lowerLimit
                      : dataMin;
                  return limit > 0 ? 0 : Math.floor(limit / 10) * 10; // Ensure that the Y-axis starts at 0 when limit > 0
                },
                (dataMax: number) =>
                  typeof prop.upperLimit !== "undefined" &&
                  prop.upperLimit > dataMax
                    ? Math.ceil(prop.upperLimit) + 100
                    : dataMax,
              ]}
              label={{ value: "Value", angle: -90, position: "insideLeft" }}
            />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="performance"
              stroke={
                prop.isOnDashboard ? "rgba(255, 99, 132, 0.5)" : "#c30010"
              }
              activeDot={{ r: 8 }}
              strokeWidth={1.5}
            />
            <Line
              type="monotone"
              dataKey="Median"
              stroke={
                prop.isOnDashboard ? "rgba(53, 162, 235, 0.5)" : "#FFBF00"
              }
              strokeWidth={2}
            />
            {typeof prop.upperLimit !== "undefined" && (
              <ReferenceLine
                y={prop.upperLimit}
                stroke="red"
                strokeDasharray="3 3"
                label={{
                  value: `UCL`,
                  position: "insideTopRight", // Adjust the label to a more visible position
                  fill: "red",
                  fontWeight: "bold",
                }}
              />
            )}
            {typeof prop.lowerLimit !== "undefined" && (
              <ReferenceLine
                y={prop.lowerLimit}
                stroke="red"
                strokeDasharray="3 3"
                label={{
                  value: `LCL`,
                  position: "insideBottomRight", // Adjust the label to a more visible position
                  fill: "red",
                  fontWeight: "bold",
                }}
              />
            )}
          </LineChart>
          {prop.chartInterpretation &&
            prop.chartInterpretationDescription &&
            prop.showInterpretation && (
              <Box
                background={"#f4f4f4"}
                width={dimensions.width / 2}
                color={color.text}
                height={dimensions.height}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"flex-start"}
                alignContent={"flex-start"}
                alignItems={"flex-start"}
                borderWidth={1}
                p={5}
                gap={2}
                borderColor={color.primary}
              >
                <Text fontWeight={"bold"} color={color.primary}>
                  Chart Analysis:
                </Text>
                <Text>{prop.chartInterpretation}</Text>
                <Text fontWeight={"bold"} color={color.primary}>
                  Description:
                </Text>
                {prop.chartInterpretationDescription.map((desc, index) => (
                  <Text sx={{ mt: 0 }}>{desc}</Text>
                ))}
              </Box>
            )}
        </HStack>
      </VStack>
    </Center>
  );
};

export default RunChart;
